import { atom } from 'jotai';
import { atomFamily, atomWithStorage, createJSONStorage } from 'jotai/utils';

export type ViewerState = {
  imageId: string | null;
  googleImageId: string | null;
  position: {
    x: number;
    y: number;
    z: number;
  };
};

export const DEFAULT_VIEWER_POSITION = {
  x: 0,
  y: 0,
  z: 0,
} as const;

// Type for the project's initial position (GeometryZ from GraphQL)
type GeometryZ = {
  coords: [number, number, number];
  type: string;
} | null;

const createDefaultState = (initialPosition?: { x: number; y: number; z: number }): ViewerState => ({
  imageId: null,
  position: initialPosition ? { ...initialPosition } : { ...DEFAULT_VIEWER_POSITION },
});

// Helper function to extract position from project's initialPosition (GeometryZ)
const extractInitialPosition = (initialPosition: GeometryZ) => {
  if (!initialPosition || !initialPosition.coords) return null;
  const [x, y, z] = initialPosition.coords;
  return { x, y, z: z || 0 };
};

const storage = createJSONStorage<ViewerState>(() => sessionStorage);

// Store to keep track of project initial positions
const projectInitialPositions = new Map<string, { x: number; y: number; z: number }>();

export const setProjectInitialPosition = (projectId: string, initialPosition: GeometryZ) => {
  const extractedPosition = extractInitialPosition(initialPosition);
  if (extractedPosition) {
    projectInitialPositions.set(projectId, extractedPosition);
  }
};

// Function to update viewer state atom with initial position if currently at default
export const updateViewerStateWithInitialPosition = (
  projectId: string,
  setViewerState: (updater: (prev: ViewerState) => ViewerState) => void,
) => {
  const projectInitialPos = projectInitialPositions.get(projectId);
  if (projectInitialPos) {
    setViewerState((prev) => {
      const isDefaultPosition =
        prev.position.x === DEFAULT_VIEWER_POSITION.x &&
        prev.position.y === DEFAULT_VIEWER_POSITION.y &&
        prev.position.z === DEFAULT_VIEWER_POSITION.z;

      if (isDefaultPosition) {
        return {
          ...prev,
          position: projectInitialPos,
        };
      }
      return prev;
    });
  }
};

export const viewerStateAtomFamily = atomFamily((projectId: string) => {
  const storageKey = `@actegon.gis.viewer.${projectId}`;

  // Custom storage that checks for project initial position on first read
  const customStorage = {
    ...storage,
    getItem: (key: string, defaultValue: ViewerState) => {
      try {
        const stored = sessionStorage.getItem(key);
        if (stored) {
          const parsedData = JSON.parse(stored) as ViewerState;

          // Check if stored position is default and we have a project initial position
          const projectInitialPos = projectInitialPositions.get(projectId);
          const isDefaultPosition =
            parsedData.position.x === DEFAULT_VIEWER_POSITION.x &&
            parsedData.position.y === DEFAULT_VIEWER_POSITION.y &&
            parsedData.position.z === DEFAULT_VIEWER_POSITION.z;

          if (isDefaultPosition && projectInitialPos) {
            // Update with project initial position
            const updatedData = {
              ...parsedData,
              position: projectInitialPos,
            };
            // Save the updated data back to storage
            try {
              sessionStorage.setItem(key, JSON.stringify(updatedData));
            } catch {
              // Ignore storage errors
            }
            return updatedData;
          }

          return parsedData;
        }

        // No stored data, check if we have project initial position
        const projectInitialPos = projectInitialPositions.get(projectId);
        if (projectInitialPos) {
          return createDefaultState(projectInitialPos);
        }

        return defaultValue;
      } catch {
        // Parse error, check if we have project initial position
        const projectInitialPos = projectInitialPositions.get(projectId);
        if (projectInitialPos) {
          return createDefaultState(projectInitialPos);
        }
        return defaultValue;
      }
    },
  };

  const persistedAtom = atomWithStorage<ViewerState>(storageKey, createDefaultState(), customStorage);

  return atom(
    (get) => get(persistedAtom),
    (get, set, updater: Partial<ViewerState> | ((prev: ViewerState) => ViewerState)) => {
      const previous = get(persistedAtom);
      const nextValue =
        typeof updater === 'function'
          ? (updater as (prev: ViewerState) => ViewerState)(previous)
          : {
              ...previous,
              ...updater,
              position: updater.position ?? previous.position,
            };

      set(persistedAtom, nextValue);
    },
  );
});

// Add Point Mode state management
export type AddPointMode = {
  enabled: boolean;
  targetLayerId: string | null;
  draftFeature: {
    geometry: { type: 'Point'; coordinates: [number, number, number] };
    properties: Record<string, any>;
  } | null;
};

export const addPointModeAtom = atom<AddPointMode>({
  enabled: false,
  targetLayerId: null,
  draftFeature: null,
});

// Typed event emitter
class EventEmitter<T extends Record<string, unknown>> {
  private listeners = new Map<keyof T, Set<(payload: any) => void>>();

  on<K extends keyof T>(event: K, fn: (payload: T[K]) => void) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(fn);
    return () => this.off(event, fn);
  }

  off<K extends keyof T>(event: K, fn: (payload: T[K]) => void) {
    this.listeners.get(event)?.delete(fn);
  }

  emit<K extends keyof T>(event: K, payload: T[K]) {
    this.listeners.get(event)?.forEach((fn) => fn(payload));
  }
}

type ViewerEvents = {
  jumpToTarget: { x: number; y: number; z: number };
};

export const viewerEvents = new EventEmitter<ViewerEvents>();

// Clip Box Mode
export const clipBoxEnabledAtom = atom(false);

export type ClipBoxMode = 'NONE' | 'CLIP_OUTSIDE' | 'CLIP_INSIDE' | 'HIGHLIGHT_INSIDE';
export const clipBoxModeAtom = atom<ClipBoxMode>('HIGHLIGHT_INSIDE');
